<!--
    This component is created by LIU student group TDP032
    in our developer portal https://developer.zoezi.se/#/show/c0214d2da7db2807 -> Components -> Anpassningsbart formulär
-->

<template>
    <div>
        <!-- Using component-wrapper we load in each field/row to display it -->
        <component-wrapper
            v-for="row in rows"
            :key="row.id"
            :config="row"
            @componentReady="componentReady"
            @fieldChange="updatedFieldValues($event)"
            :array="rows"
            can-remove
            draggable="true"
        ></component-wrapper>

        <!-- Using component-wrapper we load in each field/row to display it -->
        <div class="d-flex justify-end mt-2">
            <v-btn
                :color="submitButtonColor"
                :outlined="submitButtonOutlined"
                elevation="1"
                :disabled="invalidFormInput || sending || additionalDisabled"
                @click="send"
            >{{ translate ? $translate(submitButtonText) : submitButtonText }}</v-btn
            >
        </div>

        <!-- Dialog to show a response if send is successful -->
        <v-dialog v-model="dialog" persistent content-class="form-dialog">
            <v-card>
                <v-card-title class="text-h5">{{ $translate(formAnswerTitle) }}</v-card-title>
                <v-card-text>{{ $translate('Thank you for your message!') }}</v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" elevation="1" @click="dialog = false"
                    >{{ $translate('Ok') }}</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
export default {
    name: "zoezi-custom-form",

    zoezi: {
        title: "Customizable form",
        icon: "mdi-form-select",
    },

    props: {
        formTitle: {
            title: "Topic",
            type: String,
            default: "Title",
        },
        receivers: {
            title: "Recipients",
            type: Array,
            fulltype: "select",
            typeinfo: {
                multiple: true,
                text: "name",
                value: "id",
                placeholder: "Select staff",
                nosort: true,
                getItems: () =>
                    window.$zoeziapi.get("/api/member/get/full").then((members) =>
                        members
                            .filter((member) => member.staff)
                            .map((x) => ({
                                id: x.id,
                                name: x.firstname ? x.firstname + " " + x.lastname : x.lastname,
                                mail: x.email,
                            }))
                    ),
            },
            default: () => [],
        },
        submitButtonText: {
            title: "Text on submit button",
            type: String,
            default: "Submit",
        },
        submitButtonColor: {
            title: "Submit button color",
            type: String,
            default: "primary",
        },
        submitButtonOutlined: {
            title: "Submit button outlined",
            type: Boolean,
            default: false,
        },
        formAnswerTitle: {
            title: "Answer heading",
            type: String,
            default: "Message sent",
        },
        metaData: {
            title: "Include metadata",
            type: Boolean,
            default: false,
        },
        translate: {
            title: "Translate",
            type: Boolean,
            default: true,
        },

        isMail: {
            title: "Send as mail",
            type: Boolean,
            section: "Mail",
            default: false,
        },
        externalMail: {
            title: "External e-mail",
            type: String,
            section: "Mail",
            default: "",
            typeinfo: {
                placeholder: "example@gmail.com, example2@gmail.com",
            }
        },

        isTodo: {
            title: "Create todo",
            type: Boolean,
            section: "ToDo",
            default: false,
        },
        daysUntilDueDate: {
            title: "Days until due date",
            type: Number,
            section: "ToDo",
            default: null,
        },

        rows: {
            title: "Add or move the order of fields",
            type: Array,
            fulltype: "componentlist",
            section: "Field for form",
            typeinfo: {
                id: "",
                component:
                    "zoezi-custom-form-field",
            },
            default: () => [],
        },
        additionalDisabled: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            fieldData: [],
            dialog: false,
            sending: false,
            rowRefs: {},
        };
    },

    methods: {
        componentReady(component) {
            /*
            When component emits self through "componentReady"
            we save the reference to the component in the rowRefs object
            This allows us to call functions on the component from the parent
            ex. to reset the fields, in "resetFields()"
            */

            this.rowRefs[component.id] = component;
        },
        componentDestroyed(component) {
            /*
            When component emits self through "componentDestroyed"
            we remove the reference to the component in the rows array
            */
            delete this.rowRefs[component.id];
        },
        resetFields() {
            this.rows.forEach(row => {
                this.rowRefs[row.id].setInitialValue();
            });
        },
        updatedFieldValues(value) {
            /**
                 * Updates a fields value, this function gets called when an Emit is captured from ComponentWrapper/the field component
                 * @param {Object} value - is the changed/updated value we get from the fields Emit
                 */

            this.fieldData = this.fieldData || []; // Ensure fieldData is initialized

            // Extracting properties from `value` for easier handling in the form.
            // `changed` is obtained from `value.content`.
            // `id`, `title` (renamed from `text`), and `valid` are obtained from `value.emitter`.
            let { content: changed, emitter: { id, text: title, valid } } = value;

            let existingField = this.fieldData.find(row => row.id == id);
            if (existingField) {
                Object.assign(existingField, { content: changed, title, valid });
            } else {
                this.fieldData.push({ id, title, valid, content: changed });
            }

            // Emit the field change to parent components
            this.$emit('fieldChange', value);
        },
        send() {
            /**
                 * This function calls sendMail, sendTodo and createFormResponse based on multiple variables and props.
                 * If the Todo, Mail or Todo and Mail is successfull we open the response dialog.
                 */
            if (this.sending) {
                return;
            } else {
                this.sending = true;
            }

            let externalMail = this.externalMail ? this.externalMail.split(',').map(v => v.trim()).filter(v => v) : [];

            this.$api.post('/api/public/customform/send', {
                component_id: this.$attrs.id,
                homepage_revision_id: this.$root.current_revision_id,
                receivers: this.receivers,
                rows: this.fieldData,
                subject: this.$translate(this.formTitle),
                external_mail: externalMail,
                mail: this.isMail,
                todo: this.isTodo,
                todo_days_until_due_date: this.daysUntilDueDate,
                metadata: this.metaData ? this.userInfo : null,
            }).then(async response => {
                if (response.ok) {
                    this.dialog = true;
                } else {
                    const data = await response.json();
                    this.$store.commit("showErrorDialog", {
                        title: this.$translate("Error"),
                        text: data.message || this.$translate("Unable to send the message"),
                    });
                }
            }).finally(() => {
                this.resetFields();
                this.sending = false;
            });
        },
    },

    computed: {
        invalidFormInput() {
            /**
                 * Check to see if all the field has a valid value. This is used in the button to see if the enduser is allowed to send the form or not.
                 * returns {boolean} - all fields are valid or not
                 */

            let isGood = false;
            this.fieldData.forEach((row) => {
                if (!row.valid) {
                    isGood = true;
                }
            });

            return isGood;
        },
        userInfo() {
            /**
                 * Returns the user information that is used in the meta data.
                 */
            let info = {};

            if (this.$store.state.user) {
                info["userId"] = this.$store.state.user.id;
                info["name"] = this.$store.state.user.name;
                info["email"] = this.$store.state.user.mail;
            }
            if (this.$store.state.settings.modules.MultiSite) {
                const selectedSite = this.$store.state.settings.sites.find((site) => site.id == this.$store.state.selectedSiteId);
                info["site"] = selectedSite ? `${selectedSite?.name} (${selectedSite?.id})` : null;
            }
            info["device"] = window.navigator.userAgent;
            info["url"] = window.location.href;

            return info;
        },
    },
};
</script>

<style>
.form-dialog {
    max-width: 40rem;
}
</style>
